import Konva from 'konva';
import { characterTokenGeometry } from './character_token_geometry.js';
import { FEET_PER_CELL } from './area_effect_geometry.js';
import { TOUCH_DRAG_THRESHOLD } from './touch_gesture_methods.js';
import { distanceLabel } from './movement_distance.js';

const cell = (shape) => ({ gridColumn: shape.gridColumn, gridRow: shape.gridRow });
const sameCell = (a, b) => a && b && a.gridColumn === b.gridColumn && a.gridRow === b.gridRow;
const STEPS = { ArrowUp: [0, -1], ArrowRight: [1, 0], ArrowDown: [0, 1], ArrowLeft: [-1, 0] };

export function destinationAtPointer(point, token, grid) {
  if (!point || !Number.isFinite(grid.size) || grid.size <= 0) return null;
  // Aim at the footprint's center, including characters occupying several cells.
  const halfCells = token.sizeFeet / 10;
  return {
    gridColumn: Math.round((point.x - grid.offsetX) / grid.size - halfCells),
    gridRow: Math.round((point.y - grid.offsetY) / grid.size - halfCells)
  };
}

export function destinationDescription(from, to) {
  if (!to || sameCell(from, to)) return 'Tap a destination on the map.';
  const columns = to.gridColumn - from.gridColumn;
  const rows = to.gridRow - from.gridRow;
  const feet = Math.max(Math.abs(columns), Math.abs(rows)) * FEET_PER_CELL;
  return (
    `${feet} ft · ` +
    [
      columns && `${Math.abs(columns)} ${columns > 0 ? 'right' : 'left'}`,
      rows && `${Math.abs(rows)} ${rows > 0 ? 'down' : 'up'}`
    ]
      .filter(Boolean)
      .join(' · ') +
    ' (grid squares)'
  );
}

export const CharacterMovementMethods = {
  _movableCharacter(id) {
    const shape = this._findShapeById(id);
    return this._canEditShape(shape) && shape.type === 'character' && this.gridEnabledValue && !this.hiddenValue
      ? shape
      : null;
  },

  startCharacterMove(event) {
    event?.preventDefault();
    const shape = this._movableCharacter(event?.currentTarget.dataset.shapeId || this._selectedShapeId);
    if (!shape || !this._stage) return;
    this._cancelCanvasGesture();
    this._clearCharacterMove();
    this._selectShape(shape.id);
    this._characterMove = { id: shape.id, sizeFeet: shape.sizeFeet, from: cell(shape), to: null };
    this._hideShapePopover?.();
    this._shapeTransformer?.hide();
    this.collapseMapControls();
    this._syncCharacterMove();
    this.characterMovePanelTarget.focus({ preventScroll: true });
  },

  cancelCharacterMove(event) {
    event?.preventDefault();
    this._cancelCanvasGesture();
    this._clearCharacterMove();
    this._showCharacterControls();
  },

  confirmCharacterMove(event) {
    event?.preventDefault();
    const move = this._characterMove;
    const shape = move && this._movableCharacter(move.id);
    if (!shape || !sameCell(shape, move.from) || shape.sizeFeet !== move.sizeFeet) return this.cancelCharacterMove();
    if (!move.to || sameCell(move.from, move.to) || this._touchGesture || this._dragging) return;
    this._lastCharacterMove = { id: shape.id, from: move.from, to: move.to };
    this._setCharacterCell(shape, move.to);
    this._clearCharacterMove();
    this._showCharacterControls();
  },

  undoCharacterMove(event) {
    event?.preventDefault();
    const move = this._lastCharacterMove;
    const shape = move && this._movableCharacter(move.id);
    if (!shape || this._selectedShapeId !== move.id || !sameCell(shape, move.to)) return;
    this._lastCharacterMove = null;
    this._setCharacterCell(shape, move.from);
    this._showCharacterControls();
  },

  _setCharacterCell(shape, destination) {
    const positions = this._shapeVisualPositions?.();
    Object.assign(shape, destination);
    shape.konvaShape.setAttrs(characterTokenGeometry(shape, this._areaEffectGrid()));
    this._syncLinkedEffects(shape.id);
    this._shapeTransformer?.forceUpdate();
    this._commitShapeMove();
    this._animateShapeMovement?.(positions);
  },

  _showCharacterControls() {
    if (!this._selectedShapeId) return;
    if (!this._mobileEditor) this._showToolbar('shapes', 'select');
    this._selectShape(this._selectedShapeId);
    // The shared native popover opens after its Stimulus values update.
    requestAnimationFrame(() => {
      if (!this.element.isConnected || this._characterMove || !this.hasCharacterMovementTarget) return;
      this.characterMovementTarget
        .querySelector('[data-action$="#startCharacterMove"]')
        ?.focus({ preventScroll: true });
    });
  },

  _restoreCharacterMovement(move, undo) {
    // Polling canonicalizes local saves and also brings in other players' edits.
    // Keep a preview across those refreshes only while its source is unchanged.
    const shape = move && this._movableCharacter(move.id);
    if (shape && sameCell(shape, move.from) && shape.sizeFeet === move.sizeFeet) {
      this._characterMove = move;
      this.collapseMapControls();
      this._drawCharacterDestination();
    }
    if (undo && sameCell(this._movableCharacter(undo.id), undo.to)) this._lastCharacterMove = undo;
    this._refreshCharacterMovement();
  },

  _clearCharacterMove() {
    this._characterMove = null;
    this._characterMovePointer = null;
    this._characterMovePreview?.destroy();
    this._characterMovePreview = null;
    this._cursorLayer?.batchDraw();
    this._syncCharacterMove();
  },

  _syncCharacterMove() {
    if (!this.hasCharacterMovePanelTarget) return;
    const move = this._characterMove;
    this.characterMovePanelTarget.hidden = !move;
    this.editorTarget.dataset.characterMoving = String(!!move);
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = move ? 'crosshair' : 'default';
    if (!move) return;
    this.characterMoveNameTarget.textContent = this._findShapeById(move.id)?.name || 'Character';
    this.characterMoveStatusTarget.textContent = destinationDescription(move.from, move.to);
    this.confirmCharacterMoveTarget.disabled = !move.to || sameCell(move.from, move.to);
    this._hideShapePopover();
  },

  _refreshCharacterMovement() {
    const shape = this._findShapeById(this._selectedShapeId);
    if (this.hasUndoCharacterMoveTarget) {
      const move = this._lastCharacterMove;
      this.undoCharacterMoveTarget.disabled = !move || move.id !== shape?.id || !sameCell(shape, move.to);
    }
  },

  _onCharacterMoveDown() {
    this._onDragDown();
    this._characterMovePointer = { ...this._stage.getPointerPosition(), moved: false };
  },

  _onCharacterMovePointer() {
    const start = this._characterMovePointer;
    const point = this._stage.getPointerPosition();
    if (!start || !point) return;
    start.moved ||= Math.hypot(point.x - start.x, point.y - start.y) >= TOUCH_DRAG_THRESHOLD;
    if (start.moved) this._onDragMove();
  },

  _onCharacterMoveUp() {
    const start = this._characterMovePointer;
    this._dragging = false;
    this._characterMovePointer = null;
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'crosshair';
    if (!start || start.moved || this._touchGesture?.moved) return;
    const move = this._characterMove;
    const shape = this._movableCharacter(move.id);
    if (!shape) return this.cancelCharacterMove();
    move.to = destinationAtPointer(this._getWorldPointerPosition(), shape, this._areaEffectGrid());
    this._drawCharacterDestination();
  },

  _drawCharacterDestination() {
    this._characterMovePreview?.destroy();
    this._characterMovePreview = null;
    const move = this._characterMove;
    const shape = move && this._movableCharacter(move.id);
    if (shape && move.to && !sameCell(move.from, move.to)) {
      const grid = this._areaEffectGrid();
      const from = characterTokenGeometry({ ...shape, ...move.from }, grid);
      const to = characterTokenGeometry({ ...shape, ...move.to }, grid);
      const preview = new Konva.Group({ listening: false });
      // Only neutral movement guides sit above fog, never a copy of the avatar or effects.
      for (const [stroke, strokeWidth] of [
        ['#17212b', 6],
        ['#ffffff', 2]
      ]) {
        preview.add(
          new Konva.Line({
            points: [from.x + from.width / 2, from.y + from.height / 2, to.x + to.width / 2, to.y + to.height / 2],
            stroke,
            strokeWidth,
            strokeScaleEnabled: false,
            dash: [6, 6]
          })
        );
        preview.add(new Konva.Rect({ ...to, stroke, strokeWidth, strokeScaleEnabled: false, cornerRadius: 2 }));
      }
      const feet =
        Math.max(Math.abs(move.to.gridColumn - move.from.gridColumn), Math.abs(move.to.gridRow - move.from.gridRow)) *
        FEET_PER_CELL;
      const label = distanceLabel(feet, 'character-move-preview-distance');
      const scale = this._stage.scaleX();
      label.scale({ x: 1 / scale, y: 1 / scale });
      label.position({
        x: (from.x + to.x + from.width) / 2 - label.width() / scale / 2,
        y: (from.y + to.y + from.height) / 2 - label.height() / scale / 2
      });
      preview.add(label);
      this._characterMovePreview = preview;
      this._cursorLayer.add(preview);
      this._cursorLayer.moveToTop();
    }
    this._cursorLayer?.batchDraw();
    this._syncCharacterMove();
  },

  _onCharacterMoveKey(event) {
    if (!this._characterMove || event.altKey || event.ctrlKey || event.metaKey) return false;
    if (event.key === 'Escape') this.cancelCharacterMove();
    else if (event.key === 'Enter' && !event.target.closest('button, a')) this.confirmCharacterMove();
    else if (STEPS[event.key]) {
      const move = this._characterMove;
      const point = move.to || move.from;
      const [column, row] = STEPS[event.key];
      move.to = { gridColumn: point.gridColumn + column, gridRow: point.gridRow + row };
      this._drawCharacterDestination();
    } else return false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  }
};
